import { LoadingButton } from "@mui/lab";
import { Formik } from "formik";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  useExportBlogPostMutation,
  useGetProjectOnlineInfosMutation,
  useUpdateSingleProjectMutation,
} from "../../../redux/slices/projectApiSlice";
import { trackPlatformUsed } from "../../../components/trackers";
import { platforms } from "../helper";
import {
  ShopifySettingsForm,
  WixSettingsForm,
  WordpressSettingsForm,
} from "../platforms";
import { CustomLoader } from "../../../components";

const ProjectSettingsForm = ({
  platform_name,
  exportData,
  project,
  isExport = false,
  setProject = null,
  blogPost = null,
  isTableOfContent = false,
  setOpen = null,
}) => {
  const { t } = useTranslation();
  const [updateProject, { isLoading: updateLoading }] =
    useUpdateSingleProjectMutation();
  const [exportBlogPost, { isLoading: exportLoading }] =
    useExportBlogPostMutation();
  const [onlineDatas, setOnlineDatas] = useState(null);
  const [getOnlineDatas, { isLoading: getOnlineLoading }] =
    useGetProjectOnlineInfosMutation();
  const includeToc = isTableOfContent;

  const handleGetProjectOnline = async (id) => {
    try {
      const res = await getOnlineDatas({ id }).unwrap();
      setOnlineDatas(res);
    } catch (error) {}
  };

  useEffect(() => {
    handleGetProjectOnline(project.id);
  }, []);

  const platform = platforms.find(
    (p) => p.name.toLowerCase() === platform_name
  );

  if (getOnlineLoading || onlineDatas === null) {
    return (
      <div className="flex items-center">
        <CustomLoader />
      </div>
    );
  }

  return (
    <Formik
      initialValues={
        project && exportData ? exportData : platform.settingsInitialValues
      }
      validationSchema={platform.settingsValidationSchema}
      enableReinitialize={true}
      onSubmit={async (values) => {
        if (isExport) {
          trackPlatformUsed("blog_post", `export_to_cms`);
          // IF WE EXPORT TO THE PROJECT PLATFORM
          const blog_post = {
            ...blogPost,
            content: includeToc ? blogPost.contentWithToc : blogPost.content,
          };

          try {
            const payload = {
              id: project.id,
              blog_post: blog_post,
              export_data: values,
            };

            const res = await exportBlogPost(payload).unwrap();
            toast.success(
              t(
                "exported-blog-post-successfully",
                "Votre article de blog a été exporté avec succès !"
              )
            );
            setOpen(false);
          } catch (error) {
            if (error.status === 401) {
              toast.error(
                t(
                  "error-exporting-blog-post-401",
                  "Votre site a refusé la connexion. Vérifiez que les identifiants sont toujours corrects et que les autorisations de votre CMS n'ont pas changées."
                )
              );
            } else if (error.status === 403) {
              toast.error(
                t(
                  "error-exporting-blog-post-403",
                  "Nous n'avons pas les bonnes autorisations pour publier sur votre site. Vérifiez qu'aucun plugin de sécurité ne bloque et que les bonnes autorisations sont activées. Pour plus d'infos, consultez le tutoriel lié à votre CMS."
                )
              );
            } else {
              toast.error(
                t(
                  "error-exporting-blog-post",
                  "Une erreur a eu lieu lors de l'export de l'article de blog !"
                )
              );
            }
            setOpen(false);
          }
        } else {
          trackPlatformUsed("project", "update_infos");
          // IF WE SET PROJECT DEFAULT INFOS
          try {
            const res = await updateProject({
              ...project,
              export_data: values,
            }).unwrap();
            const updatedProject = {
              ...res.data,
              export_data: {
                ...res.data.export_data,
                _updatedAt: new Date().toISOString(),
              },
              _updatedAt: new Date().toISOString(),
            };
            setProject(updatedProject);
            toast.success(
              t(
                "modified-project-settings",
                "Les changements apportés au projet ont été sauvegardés !"
              )
            );
          } catch (error) {
            toast.error(
              t(
                "error-modifying-project-settings",
                "Une erreur a eu lieu lors de l'enregistrement des changements !"
              )
            );
          }
        }
      }}
    >
      {(props) => (
        <form onSubmit={props.handleSubmit} method="POST">
          {/* LOGO PREVIEW */}
          {isExport && (
            <div className="flex items-center justify-between py-3">
              <div className="flex items-center">
                {/* Favicon */}
                <img
                  src={project.favicon}
                  alt={`${project.name} favicon`}
                  className="w-10 h-10 mr-4"
                />
                <div>
                  {/* Name */}
                  <h1 className="text-4xl text-black font-bold font-Roboto">
                    {project.name}
                  </h1>
                  {/* URL */}
                  <a
                    href={project.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray hover:underline hover:text-black text-md"
                  >
                    {project.url}
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* FORM FIELDS */}
          {platform?.name === "WordPress" ? (
            <WordpressSettingsForm
              props={props}
              onlineDatas={onlineDatas}
              isExport={isExport}
            />
          ) : platform?.name === "Shopify" ? (
            <ShopifySettingsForm
              props={props}
              onlineDatas={onlineDatas}
              isExport={isExport}
            />
          ) : (
            platform?.name === "Wix" && (
              <WixSettingsForm
                props={props}
                onlineDatas={onlineDatas}
                isExport={isExport}
              />
            )
          )}

          {/* EXPORT/SAVE BUTTON */}
          <LoadingButton
            type="submit"
            loading={updateLoading || exportLoading || props.isSubmitting}
            variant="contained"
            sx={{
              textTransform: "none",
              bgcolor: "#8b5cf6",
              "&:hover": {
                bgcolor: "#7c3aed",
              },
            }}
          >
            {isExport
              ? "Exporter l'article vers le CMS"
              : "Sauvegarder les changements"}
          </LoadingButton>
        </form>
      )}
    </Formik>
  );
};

export default ProjectSettingsForm;
